/**
 * Media validation utilities for different content types
 */

import {
  INSTAGRAM_LIMITS,
  type RatioWindow,
  VERTICAL_VIDEO_RATIO,
  isNearVerticalRatio,
  ratioInWindow,
} from '../../shared/meta-media-limits';

import { ContentType } from '@/types/contentTypes';

export interface MediaValidationResult {
  isValid: boolean;
  error?: string;
  warnings?: string[];
}

export interface MediaConstraints {
  maxFileSize: number;
  maxDuration?: number;
  minDuration?: number;
  aspectRatio?: {
    width: number;
    height: number;
    tolerance?: number; // Tolerance percentage for aspect ratio (default 5%)
  };
  /**
   * Meta-style rule: the provider accepts anything inside this window, so a
   * ratio outside it blocks, and `recommendedAspectRatio` (9:16) only warns.
   * Used instead of `aspectRatio` for Instagram Reels and Stories.
   */
  acceptRatio?: RatioWindow;
  recommendedAspectRatio?: { width: number; height: number };
  supportedFormats: string[];
}

// Platform and content type specific constraints using enum
export const MEDIA_CONSTRAINTS: Record<ContentType, Record<string, MediaConstraints>> = {
  [ContentType.SOCIAL_POST]: {
    image: {
      // 15MB raw-accept ceiling (competitor band 5-25MB; uploads are
      // auto-compressed to JPEG ≤2MB @2048px before this check, so only
      // compression-skipped formats — gif/svg/heic — actually hit it).
      maxFileSize: 15 * 1024 * 1024, // 15MB
      supportedFormats: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    },
    video: {
      // 100MB matches Sprout's cap; every target platform either caps
      // duration harder (X: 140s) or transcodes on ingest anyway.
      maxFileSize: 100 * 1024 * 1024, // 100MB
      maxDuration: 60, // 60 seconds for regular posts
      supportedFormats: ['video/mp4', 'video/quicktime', 'video/webm'],
    },
  },
  [ContentType.INSTAGRAM_STORY]: {
    image: {
      // Meta takes JPEG at 8 MB. PNG and WebP are accepted here because
      // Mora re-encodes images to JPEG when it publishes to Instagram.
      maxFileSize: INSTAGRAM_LIMITS.image.maxMB * 1024 * 1024,
      acceptRatio: INSTAGRAM_LIMITS.story.ratio,
      recommendedAspectRatio: VERTICAL_VIDEO_RATIO,
      supportedFormats: ['image/jpeg', 'image/png', 'image/webp'],
    },
    video: {
      maxFileSize: INSTAGRAM_LIMITS.story.maxMB * 1024 * 1024,
      maxDuration: INSTAGRAM_LIMITS.story.maxSeconds,
      minDuration: INSTAGRAM_LIMITS.story.minSeconds,
      acceptRatio: INSTAGRAM_LIMITS.story.ratio,
      recommendedAspectRatio: VERTICAL_VIDEO_RATIO,
      supportedFormats: ['video/mp4', 'video/quicktime'],
    },
  },
  [ContentType.FACEBOOK_STORY]: {
    image: {
      maxFileSize: 30 * 1024 * 1024, // 30MB
      aspectRatio: { width: 9, height: 16, tolerance: 10 },
      supportedFormats: ['image/jpeg', 'image/png', 'image/webp'],
    },
    video: {
      maxFileSize: 50 * 1024 * 1024, // 50MB
      maxDuration: 20, // 20 seconds for Facebook stories
      minDuration: 1,
      aspectRatio: { width: 9, height: 16, tolerance: 10 },
      supportedFormats: ['video/mp4', 'video/quicktime'],
    },
  },
  [ContentType.INSTAGRAM_REEL]: {
    video: {
      maxFileSize: INSTAGRAM_LIMITS.reel.maxMB * 1024 * 1024,
      maxDuration: INSTAGRAM_LIMITS.reel.maxSeconds,
      minDuration: INSTAGRAM_LIMITS.reel.minSeconds,
      acceptRatio: INSTAGRAM_LIMITS.reel.ratio,
      recommendedAspectRatio: VERTICAL_VIDEO_RATIO,
      supportedFormats: ['video/mp4', 'video/quicktime'],
    },
  },
  // TikTok Content Posting API (media transfer guide, read 2026-11-03): MP4, MOV
  // or WebM, up to 4 GB, up to 10 minutes through the API (a creator's own
  // `max_video_post_duration_sec` can be lower, which readiness checks), no
  // aspect ratio rule. Must agree with PLATFORM_SPECS.tiktok and the TikTok
  // branch of shared/posting-readiness.ts: src/constants/__tests__/tiktok-limits.guard.test.ts.
  [ContentType.TIKTOK_VIDEO]: {
    video: {
      maxFileSize: 4 * 1024 * 1024 * 1024, // 4 GB
      maxDuration: 600, // 10 minutes
      minDuration: 3,
      supportedFormats: ['video/mp4', 'video/quicktime', 'video/webm'],
    },
  },
  [ContentType.CAROUSEL]: {
    image: {
      maxFileSize: 10 * 1024 * 1024, // 10MB per image
      aspectRatio: { width: 1, height: 1, tolerance: 10 }, // Square recommended
      supportedFormats: ['image/jpeg', 'image/png', 'image/webp'],
    },
  },
};

/**
 * Validates a file against the constraints for a specific content type
 */
/**
 * Synchronous size + format check only — no video/image metadata probing.
 * `mediaService.uploadFile()` calls this directly, so it's the single
 * enforcement point for every upload path (Library, Projects, the manual
 * Post editor, and AI-generated content). `validateMediaFile` below runs
 * this first, then adds the duration/aspect-ratio checks that require
 * loading the file as media.
 */
export function validateFileSizeAndFormat(
  file: File,
  contentType: ContentType,
  mediaType: 'image' | 'video',
): MediaValidationResult {
  const constraints = MEDIA_CONSTRAINTS[contentType]?.[mediaType];

  if (!constraints) {
    return {
      isValid: false,
      error: `${mediaType} not supported for ${contentType}`,
    };
  }

  if (!constraints.supportedFormats.includes(file.type)) {
    return {
      isValid: false,
      error: `Unsupported format. Please use: ${constraints.supportedFormats.join(', ')}`,
    };
  }

  if (file.size > constraints.maxFileSize) {
    const maxSizeMB = Math.round(constraints.maxFileSize / (1024 * 1024));
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  return { isValid: true };
}

export async function validateMediaFile(
  file: File,
  contentType: ContentType,
  mediaType: 'image' | 'video',
): Promise<MediaValidationResult> {
  const sizeAndFormat = validateFileSizeAndFormat(file, contentType, mediaType);
  if (!sizeAndFormat.isValid) return sizeAndFormat;

  const constraints = MEDIA_CONSTRAINTS[contentType]![mediaType]!;
  const warnings: string[] = [];

  // Video-specific validation
  if (mediaType === 'video') {
    try {
      const videoValidation = await validateVideoFile(file, constraints);
      if (!videoValidation.isValid) {
        return videoValidation;
      }
      warnings.push(...(videoValidation.warnings || []));
    } catch {
      return {
        isValid: false,
        error: 'Unable to validate video properties. Please try a different file.',
      };
    }
  }

  // Image-specific validation
  if (mediaType === 'image' && (constraints.aspectRatio || constraints.acceptRatio)) {
    try {
      const imageValidation = await validateImageFile(file, constraints);
      if (!imageValidation.isValid) {
        return imageValidation;
      }
      warnings.push(...(imageValidation.warnings || []));
    } catch {
      warnings.push('Could not validate image aspect ratio');
    }
  }

  return {
    isValid: true,
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Window rule for constraints that carry `acceptRatio`: blocks only outside
 * the provider's accept window; anything off the recommended 9:16 is a warning.
 */
function checkRatioWindow(
  ratio: number,
  constraints: MediaConstraints,
  subject: 'Video' | 'Image',
): { error?: string; warning?: string } {
  if (!constraints.acceptRatio) return {};
  if (!ratioInWindow(ratio, constraints.acceptRatio)) {
    return {
      error: `${subject} aspect ratio must be between ${constraints.acceptRatio.min}:1 and ${constraints.acceptRatio.max}:1`,
    };
  }
  if (constraints.recommendedAspectRatio && !isNearVerticalRatio(ratio)) {
    return {
      warning: `${subject} aspect ratio is not ${constraints.recommendedAspectRatio.width}:${constraints.recommendedAspectRatio.height}. It will post, with bars or a crop.`,
    };
  }
  return {};
}

/**
 * Validates video duration and aspect ratio
 */
async function validateVideoFile(
  file: File,
  constraints: MediaConstraints,
): Promise<MediaValidationResult> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);

      const duration = video.duration;
      const aspectRatio = video.videoWidth / video.videoHeight;
      const warnings: string[] = [];

      // Duration validation
      if (constraints.maxDuration && duration > constraints.maxDuration) {
        resolve({
          isValid: false,
          error: `Video too long. Maximum duration: ${constraints.maxDuration} seconds`,
        });
        return;
      }

      if (constraints.minDuration && duration < constraints.minDuration) {
        resolve({
          isValid: false,
          error: `Video too short. Minimum duration: ${constraints.minDuration} seconds`,
        });
        return;
      }

      const windowCheck = checkRatioWindow(aspectRatio, constraints, 'Video');
      if (windowCheck.error) {
        resolve({ isValid: false, error: windowCheck.error });
        return;
      }
      if (windowCheck.warning) warnings.push(windowCheck.warning);

      // Aspect ratio validation
      if (constraints.aspectRatio) {
        const expectedRatio = constraints.aspectRatio.width / constraints.aspectRatio.height;
        const tolerance = (constraints.aspectRatio.tolerance || 5) / 100;
        const difference = Math.abs(aspectRatio - expectedRatio) / expectedRatio;

        if (difference > tolerance) {
          const ratioText = `${constraints.aspectRatio.width}:${constraints.aspectRatio.height}`;
          resolve({
            isValid: false,
            error: `Video aspect ratio should be ${ratioText} (current: ${video.videoWidth}x${video.videoHeight})`,
          });
          return;
        } else if (difference > tolerance / 2) {
          warnings.push(
            `Video aspect ratio is close to ${constraints.aspectRatio.width}:${constraints.aspectRatio.height} but may appear cropped`,
          );
        }
      }

      resolve({
        isValid: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        error: 'Could not read video file',
      });
    };

    video.src = url;
  });
}

/**
 * Validates image aspect ratio
 */
async function validateImageFile(
  file: File,
  constraints: MediaConstraints,
): Promise<MediaValidationResult> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const aspectRatio = img.naturalWidth / img.naturalHeight;
      const warnings: string[] = [];

      const windowCheck = checkRatioWindow(aspectRatio, constraints, 'Image');
      if (windowCheck.error) {
        resolve({ isValid: false, error: windowCheck.error });
        return;
      }
      if (windowCheck.warning) warnings.push(windowCheck.warning);

      // Aspect ratio validation
      if (constraints.aspectRatio) {
        const expectedRatio = constraints.aspectRatio.width / constraints.aspectRatio.height;
        const tolerance = (constraints.aspectRatio.tolerance || 5) / 100;
        const difference = Math.abs(aspectRatio - expectedRatio) / expectedRatio;

        if (difference > tolerance) {
          const ratioText = `${constraints.aspectRatio.width}:${constraints.aspectRatio.height}`;
          resolve({
            isValid: false,
            error: `Image aspect ratio should be ${ratioText} (current: ${img.naturalWidth}x${img.naturalHeight})`,
          });
          return;
        } else if (difference > tolerance / 2) {
          warnings.push(
            `Image aspect ratio is close to ${constraints.aspectRatio.width}:${constraints.aspectRatio.height} but may appear cropped`,
          );
        }
      }

      resolve({
        isValid: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        error: 'Could not read image file',
      });
    };

    img.src = url;
  });
}

/**
 * Gets content type constraints for UI display
 */
export function getContentTypeConstraints(contentType: ContentType): {
  supportedMedia: ('image' | 'video')[];
  constraints: Record<string, MediaConstraints>;
} {
  const constraints = MEDIA_CONSTRAINTS[contentType] || {};
  return {
    supportedMedia: Object.keys(constraints) as ('image' | 'video')[],
    constraints,
  };
}
